import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, View, Text, Image, TouchableHighlight, Platform, Animated, Keyboard } from 'react-native';
import Style from './rn-datepicker.style';
import Moment from 'moment';

const FORMATS = {
	date: 'YYYY-MM-DD',
	datetime: 'YYYY-MM-DD HH:mm',
	time: 'HH:mm',
	displayFormat: 'ddd DD/MM/YY'
};

const SUPPORTED_ORIENTATIONS = ['portrait', 'portrait-upside-down', 'landscape', 'landscape-left', 'landscape-right'];

class DatePicker extends Component {
	constructor(props) {
		super(props);

		this.state = {
			date: this.getDate(),
			modalVisible: false,
			animatedHeight: new Animated.Value(0),
			allowPointerEvents: true,
			webTypingDate: null
		};

		this.getDate = this.getDate.bind(this);
		this.getDateStr = this.getDateStr.bind(this);
		this.datePicked = this.datePicked.bind(this);
		this.onPressDate = this.onPressDate.bind(this);
		this.onPressCancel = this.onPressCancel.bind(this);
		this.onPressConfirm = this.onPressConfirm.bind(this);
		this.onDateChange = this.onDateChange.bind(this);
		this.onPressMask = this.onPressMask.bind(this);
		this.onDatePicked = this.onDatePicked.bind(this);
		this.onTimePicked = this.onTimePicked.bind(this);
		this.onDatetimePicked = this.onDatetimePicked.bind(this);
		this.onDatetimeTimePicked = this.onDatetimeTimePicked.bind(this);
		this.setModalVisible = this.setModalVisible.bind(this);
	}

	componentWillMount() {
		// ignore the warning of Failed propType for date of DatePickerIOS, will remove after being fixed by official
		console.ignoredYellowBox = [
			'Warning: Failed propType'
			// Other warnings you don't want like 'jsSchedulingOverhead',
		];
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.date !== this.props.date) {
			this.setState({ date: this.getDate(nextProps.date) });
		}
	}

	setModalVisible(visible) {
		const { height, duration } = this.props;

		// slide animation
		if (visible) {
			this.setState({ modalVisible: visible });
			return Animated.timing(this.state.animatedHeight, {
				toValue: height,
				duration: duration
			}).start();
		} else {
			return Animated.timing(this.state.animatedHeight, {
				toValue: 0,
				duration: duration
			}).start(() => {
				this.setState({ modalVisible: visible });
			});
		}
	}

	onStartShouldSetResponder(e) {
		return true;
	}

	onMoveShouldSetResponder(e) {
		return true;
	}

	onPressMask() {
		if (typeof this.props.onPressMask === 'function') {
			this.props.onPressMask();
		} else {
			this.onPressCancel();
		}
	}

	onPressCancel() {
		this.setModalVisible(false);

		if (typeof this.props.onCloseModal === 'function') {
			this.props.onCloseModal();
		}
	}

	onPressConfirm() {
		this.datePicked();
		this.setModalVisible(false);

		if (typeof this.props.onCloseModal === 'function') {
			this.props.onCloseModal();
		}
	}

	getDate(date = this.props.date) {
		const { mode, minDate, maxDate, format = FORMATS[mode] } = this.props;

		// date默认值
		if (!date) {
			let now = new Date();
			if (minDate) {
				let _minDate = this.getDate(minDate);

				if (now < _minDate) {
					return _minDate;
				}
			}

			if (maxDate) {
				let _maxDate = this.getDate(maxDate);

				if (now > _maxDate) {
					return _maxDate;
				}
			}

			return now;
		}

		if (date instanceof Date) {
			return date;
		}

		return Moment(date, format).toDate();
	}

	getDateStr(date = this.props.date) {
		const { mode, displayFormat = FORMATS[mode] } = this.props;
		if (date instanceof Date) {
			return Moment(date).format(displayFormat);
		} else {
			return Moment(this.getDate(date)).format(displayFormat);
		}
	}

	datePicked() {
		//console.log('datePicked', this.state.date);
		if (typeof this.props.onDateChange === 'function') {
			this.props.onDateChange(this.state.date);
		}
	}

	getTitleElement() {
		const { date, placeholder, customStyles, showIcon } = this.props;

		if (!date && placeholder) {
			return <Text style={[Style.placeholderText, customStyles.placeholderText]}>{placeholder}</Text>;
		}

		//console.log(this.state.webTypingDate);
		const properVal = this.state.webTypingDate || Moment(this.getDate()).format(FORMATS.date);
		return (
			<input
				type="date"
				style={{ borderWidth: 0, backgroundColor: 'transparent', fontSize: 17, fontFamily: 'Segoe UI' }}
				value={properVal}
				className={showIcon && 'showicon'}
				onBlur={e => this.webDateChange(e)}
				onChange={event => this.setState({ webTypingDate: event.target.value })}
			/>
		);
	}

	onDateChange(date) {
		//console.log('date', date);
		this.setState({
			allowPointerEvents: false,
			date: date
		});
		const timeoutId = setTimeout(() => {
			this.setState({
				allowPointerEvents: true
			});
			clearTimeout(timeoutId);
		}, 200);
	}

	webDateChange(event) {
		//console.log(event, event.target.valueAsDate);
		event.target && this.setState({ date: event.target.valueAsDate }, () => this.datePicked());
	}

	onDatePicked({ action, year, month, day }) {
		//console.log('datepicked', arguments, new Date(year, month, day));

		this.setState({
			date: new Date(year, month, day)
		});
		this.datePicked();
	}

	onTimePicked({ action, hour, minute }) {
		this.setState({
			date: Moment().hour(hour).minute(minute).toDate()
		});
		this.datePicked();
	}

	onDatetimePicked({ action, year, month, day }) {
		const { mode, androidMode, format = FORMATS[mode], is24Hour = !format.match(/h|a/) } = this.props;

		let timeMoment = Moment(this.state.date);

		TimePickerAndroid.open({
			hour: timeMoment.hour(),
			minute: timeMoment.minutes(),
			is24Hour: is24Hour,
			mode: androidMode
		}).then(this.onDatetimeTimePicked.bind(this, year, month, day));
	}

	onDatetimeTimePicked(year, month, day, { action, hour, minute }) {
		this.setState({
			date: new Date(year, month, day, hour, minute)
		});
		this.datePicked();
	}

	onPressDate() {
		if (this.props.disabled) {
			__DEV__ && console.log('disabled date');
			return true;
		}

		Keyboard.dismiss();

		// reset state
		this.setState({
			date: this.getDate()
		});

		return false;
	}

	_renderIcon() {
		const { showIcon, iconSource, iconComponent, customStyles } = this.props;

		return null;
	}

	render() {
		const {
			mode,
			style,
			customStyles,
			disabled,
			minDate,
			maxDate,
			minuteInterval,
			timeZoneOffsetInMinutes,
			cancelBtnText,
			confirmBtnText,
			TouchableComponent,
			testID,
			cancelBtnTestID,
			confirmBtnTestID
		} = this.props;

		const dateInputStyle = [Style.dateInput, customStyles.dateInput, disabled && Style.disabled, disabled && customStyles.disabled];

		return (
			<TouchableComponent style={[Style.dateTouch, style]} underlayColor={'transparent'} onPress={this.onPressDate} testID={testID}>
				<View style={[Style.dateTouchBody, customStyles.dateTouchBody]}>
					{this._renderIcon()}
					{!this.props.hideText ? <View style={dateInputStyle}>{this.getTitleElement()}</View> : <View />}
				</View>
			</TouchableComponent>
		);
	}
}

DatePicker.defaultProps = {
	mode: 'date',
	androidMode: 'default',
	date: '',
	// component height: 216(DatePickerIOS) + 1(borderTop) + 42(marginTop), IOS only
	height: 259,

	// slide animation duration time, default to 300ms, IOS only
	duration: 300,
	confirmBtnText: 'OK',
	cancelBtnText: 'Cancel',
	iconSource: require('../../assets/date_icon.png'),
	customStyles: {},

	// whether or not show the icon
	showIcon: true,
	disabled: false,
	hideText: false,
	placeholder: '',
	TouchableComponent: TouchableHighlight,
	modalOnResponderTerminationRequest: e => true
};

DatePicker.propTypes = {
	mode: PropTypes.oneOf(['date', 'datetime', 'time']),
	androidMode: PropTypes.oneOf(['calendar', 'spinner', 'default']),
	date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
	format: PropTypes.string,
	displayFormat: PropTypes.string,
	minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
	maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
	height: PropTypes.number,
	duration: PropTypes.number,
	confirmBtnText: PropTypes.string,
	cancelBtnText: PropTypes.string,
	iconSource: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
	iconComponent: PropTypes.element,
	customStyles: PropTypes.object,
	showIcon: PropTypes.bool,
	disabled: PropTypes.bool,
	onDateChange: PropTypes.func,
	onOpenModal: PropTypes.func,
	onCloseModal: PropTypes.func,
	onPressMask: PropTypes.func,
	placeholder: PropTypes.string,
	modalOnResponderTerminationRequest: PropTypes.func,
	is24Hour: PropTypes.bool
};

export default DatePicker;
